import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	STR_RECIPE_KEYS,
	useSetStraightenerRecipePlcCurrent,
} from "../../straightener-recipes/hooks/useStraightenerRecipes";
import { useStraightenerParameters } from "../../straightener-common/hooks/useStraightenerParameters";
import type { StraightenerTrendRow } from "../models/StraightenerTrend";
import { STR_TREND_KEYS } from "./useStraightenerTrend";
import { sendStraightenerRecipeToPlc } from "../repositories/StraightenerTrendRepository";

interface MutationOptions {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
}

// Mirrors useSetStraightenerRecipePlcCurrent: enqueues the PLC writes. Its
// success only means the queue accepted the rows — NOT that the PLC applied
// them (see useSendToPlc for readback confirmation).
export const useSendStraightenerRecipeToPlc = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (recipeId: number) => sendStraightenerRecipeToPlc(recipeId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
			queryClient.invalidateQueries({ queryKey: STR_RECIPE_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return { sendToPlc: mutation.mutate, isSending: mutation.isPending };
};

export type SendToPlcState =
	| "idle"
	| "sending"
	| "confirming"
	| "in_plc"
	| "failed"
	| "timed_out";

export interface ParamMatch {
	parameterCode: string;
	displayName: string;
	rollNumber: number; // 0 = global
	recipe: number | null;
	current: number | null;
	scale: number | null;
	matched: boolean;
}

// No full readback match after this long ⇒ timed_out.
const CONFIRM_TIMEOUT_MS = 45_000;

// Readback tolerance in engineering units: the catalog Tolerance when set,
// half a register unit (0.5 / scale) otherwise.
const withinTolerance = (
	current: number | null,
	recipe: number | null,
	scale: number,
	tolerance: number | null,
): boolean => {
	if (current == null || recipe == null) return false;
	return Math.abs(current - recipe) <= (tolerance ?? 0.5 / scale);
};

// The transfer state machine for "Send to PLC".
//
// idle → sending → confirming → in_plc | failed | timed_out
//
// send() enqueues the writes (fire-and-forget) and moves to `confirming`. From
// there confirmation is READBACK-based: on every trend refetch we compare each
// sendToPlc param+roll that has a recipe value (Current vs Recipe within
// tolerance). When ALL compared params match we flip IsPlcCurrent via
// SetStraightenerRecipePlcCurrent and land on `in_plc`. Mutation error ⇒
// `failed`; no full match within CONFIRM_TIMEOUT_MS ⇒ `timed_out`.
//
// NOTE: this only does real work once the Send-to-PLC button is un-hidden and
// the client's write map (N200 nodes) is populated — until then the queue
// enqueues nothing and the readback never changes, so it will simply time out.
export const useSendToPlc = ({
	recipeId,
	rows,
}: {
	recipeId: number | null;
	rows: StraightenerTrendRow[];
}) => {
	const [state, setState] = useState<SendToPlcState>("idle");
	const [error, setError] = useState<string | null>(null);
	const { setPlcCurrent } = useSetStraightenerRecipePlcCurrent();
	// Guard against firing SetStraightenerRecipePlcCurrent more than once.
	const confirmedRef = useRef(false);

	const { sendToPlc } = useSendStraightenerRecipeToPlc({
		onSuccess: () => setState("confirming"),
		onError: (e) => {
			setError(e.message);
			setState("failed");
		},
	});

	// Per-param readback comparison — recomputed on each trend refetch.
	const { byCode } = useStraightenerParameters();
	const matches = useMemo<ParamMatch[]>(() => {
		const out: ParamMatch[] = [];
		for (const r of rows) {
			const p = byCode.get(r.parameterCode);
			if (!p || !p.sendToPlc || p.readOnly) continue;
			if (r.recipe == null) continue;
			out.push({
				parameterCode: r.parameterCode,
				displayName: p.displayName,
				rollNumber: r.rollNumber,
				recipe: r.recipe,
				current: r.current,
				scale: p.scale,
				matched: withinTolerance(r.current, r.recipe, p.scale, p.tolerance),
			});
		}
		return out;
	}, [rows, byCode]);

	const allMatched = matches.length > 0 && matches.every((m) => m.matched);

	// Confirmation: once every compared param reads back matched, commit.
	useEffect(() => {
		if (state !== "confirming" || confirmedRef.current) return;
		if (allMatched && recipeId != null) {
			confirmedRef.current = true;
			setPlcCurrent(recipeId);
			setState("in_plc");
		}
	}, [state, allMatched, recipeId, setPlcCurrent]);

	// Timeout window for the confirming phase.
	useEffect(() => {
		if (state !== "confirming") return;
		const t = setTimeout(() => {
			setState((s) => (s === "confirming" ? "timed_out" : s));
		}, CONFIRM_TIMEOUT_MS);
		return () => clearTimeout(t);
	}, [state]);

	const send = useCallback(() => {
		if (recipeId == null) return;
		confirmedRef.current = false;
		setError(null);
		setState("sending");
		sendToPlc(recipeId);
	}, [recipeId, sendToPlc]);

	const reset = useCallback(() => {
		confirmedRef.current = false;
		setError(null);
		setState("idle");
	}, []);

	return { state, error, matches, allMatched, send, reset };
};
